import { useContext, useEffect, useState, useCallback, useMemo, useRef } from 'react';
import {
	Phone,
	MessageSquare,
	MapPin,
	Mail,
	Search,
	ArrowDown,
	ArrowUp,
	ArrowUpDown,
	ChevronDown,
	ChevronLeft,
	ChevronRight,
	AlertTriangle,
	CheckCircle2,
	Clock,
	Users,
	DollarSign,
	TrendingUp,
	FileText,
	Target
} from 'lucide-react';
import { WhatsApp } from '@material-ui/icons';
import { GlobalContext } from '../context/GlobalContext';
import { unsafeAPI } from '../hooks/api';
import { Button } from '../shadcn/Button';
import { Dialog } from '../components/Dialog';
import { ContainerSpinner } from '../components/Spinner';
import { Skeleton } from '../components/Skeleton';
import { TabBar } from '../components/TabBar';
import { API_BASE } from '../utils/constants';
import { format_date } from '../utils/formatters';

interface CollectionUser {
	user_id: string;
	identification_number: string;
	full_name: string;
	phone_number: string;
	email: string;
	device_model: string;
	imei: string;
	total_debt: number;
	paid_installments: number;
	total_installments: number;
	missing_payments: number;
	days_overdue: number;
	sucursal: string;
	date_bought: string;
	debt_date: string;
	user_level: string;
	device_count: number;
	store_user?: string;
	seller_assignment?: string;
	status?: string;
}

interface UserDetail extends CollectionUser {
	status: string;
	follow_up_date: string | null;
	contact_count: number;
	last_contact_date: string | null;
	has_active_promise: boolean;
	contacts: Contact[];
	contacts_has_more?: boolean;
	notes: Note[];
	notes_has_more?: boolean;
	promises: Promise[];
	promises_has_more?: boolean;
}

interface Contact {
	id: string;
	type: string;
	notes: string;
	date: string;
	registered_by: string;
}

interface Note {
	id: string;
	text: string;
	date: string;
	author: string;
}

interface Promise {
	id: string;
	amount: number;
	promised_date: string;
	status: string;
	created_at: string;
	updated_at: string;
}

interface Stats {
	current_month: string;
	total_assigned: number;
	by_status: Record<string, number>;
	total_contacted: number;
	total_recovered: number;
	earnings: {
		fee_per_recovery: number;
		total_earned: number;
		potential_if_all_recovered: number;
		potential_with_promises: number;
	};
	monthly_goal: { target: number; current: number; percent: number };
	streak: { days: number; type: string };
	follow_ups: { today: number; this_week: number; overdue: number };
}

interface Earnings {
	month: string;
	fee_per_recovery: number;
	total_earned: number;
	total_recovered: number;
	recoveries: Array<{
		user_id: string;
		user_name: string;
		device: string;
		recovered_at: string;
		fee_earned: number;
	}>;
	potential: { if_all_recovered: number; with_active_promises: number };
}

interface HistoryData {
	months: Array<{
		month: string;
		label: string;
		assigned: number;
		contacted: number;
		recovered: number;
		recovery_rate: number;
		earned: number;
	}>;
	totals: {
		total_assigned: number;
		total_contacted: number;
		total_recovered: number;
		overall_recovery_rate: number;
		total_earned: number;
	};
	averages: { avg_recovered_per_month: number; avg_earned_per_month: number };
}

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
	nuevo: { label: 'Nuevo', color: 'text-primary-400', bg: 'bg-primary-500/10' },
	contactado: { label: 'Contactado', color: 'text-amber-400', bg: 'bg-amber-500/10' },
	promesa: { label: 'Promesa de pago', color: 'text-violet-400', bg: 'bg-violet-500/10' },
	recuperado: { label: 'Recuperado', color: 'text-emerald-400', bg: 'bg-emerald-500/10' }
};

const CONTACT_TYPES = [
	{ value: 'llamada', label: 'Llamada', Icon: Phone },
	{ value: 'whatsapp', label: 'WhatsApp', Icon: MessageSquare },
	{ value: 'visita', label: 'Visita', Icon: MapPin },
	{ value: 'sms', label: 'SMS', Icon: Mail }
];

const COLUMN_INDEX: Record<string, number> = {
	'ID Usuario': 0,
	Cedula: 1,
	'Nombre Completo': 2,
	Telefono: 3,
	Email: 4,
	Dispositivo: 5,
	IMEI: 6,
	'Deuda Vencida': 7,
	'Deuda Total': 7,
	'Cuotas Pagadas': 8,
	'Cuotas Totales': 9,
	'Cuotas Vencidas': 10,
	'Dias Vencido': 11,
	Sucursal: 12,
	'Fecha Compra': 13,
	Estado: 14,
	Nivel: 15,
	'Fecha Deuda': 16,
	'Cantidad Equipos': 17,
	Vendedor: 18,
	'Asignación Vendedor': 19
};

const DEFAULT_LEVEL_OPTIONS = ['level_1', 'level_2', 'level_3', 'level_4'];
const LEVEL_LABELS: Record<string, string> = {
	level_1: 'Azul',
	level_2: 'Plata',
	level_3: 'Oro',
	level_4: 'Platino'
};
const STATUS_VALUES = new Set(Object.keys(STATUS_CONFIG));
const SELLER_ASSIGNMENT_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
	inactive_seller: { label: 'Vendedor inactivo', color: 'text-amber-400', bg: 'bg-amber-500/10' },
	unassigned: { label: 'Sin vendedor', color: 'text-neutral-400', bg: 'bg-neutral-500/10' },
	mixed: { label: 'Incluye otra cuenta', color: 'text-violet-400', bg: 'bg-violet-500/10' }
};
const SORT_OPTIONS = [
	{ value: 'debt_date_asc', label: 'Fecha de deuda: más antigua', column: 'Fecha Deuda', direction: 'ASC' },
	{ value: 'debt_date_desc', label: 'Fecha de deuda: más reciente', column: 'Fecha Deuda', direction: 'DESC' },
	{ value: 'debt_desc', label: 'Deuda vencida: mayor a menor', column: 'Deuda Vencida', direction: 'DESC' },
	{ value: 'debt_asc', label: 'Deuda vencida: menor a mayor', column: 'Deuda Vencida', direction: 'ASC' },
	{ value: 'payments_desc', label: 'Cuotas vencidas: mayor a menor', column: 'Cuotas Vencidas', direction: 'DESC' },
	{ value: 'payments_asc', label: 'Cuotas vencidas: menor a mayor', column: 'Cuotas Vencidas', direction: 'ASC' }
];
const SORT_CONTROLS = [
	{ id: 'date', label: 'Fecha', column: 'Fecha Deuda', asc: 'debt_date_asc', desc: 'debt_date_desc', defaultDirection: 'ASC' },
	{ id: 'payments', label: '# cuotas', column: 'Cuotas Vencidas', asc: 'payments_asc', desc: 'payments_desc', defaultDirection: 'DESC' },
	{ id: 'debt', label: 'Monto vencido', column: 'Deuda Vencida', asc: 'debt_asc', desc: 'debt_desc', defaultDirection: 'DESC' }
];

function getColumn(row: any[], columnNames: string[] | undefined, names: string[], fallbackIndex: number) {
	if (columnNames?.length) {
		const index = names.map((name) => columnNames.indexOf(name)).find((i) => i >= 0);
		if (index !== undefined && index >= 0) return row[index];
	}
	return row[fallbackIndex];
}

function textValue(value: unknown): string {
	if (value === null || value === undefined) return '';
	return String(value);
}

function numberValue(value: unknown): number {
	if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
	const parsed = parseFloat(String(value ?? ''));
	return Number.isFinite(parsed) ? parsed : 0;
}

function formatCollectionCurrency(amount: number, currencyCode = '$'): string {
	const symbol = currencyCode || '$';
	const separator = symbol === '$' || symbol.endsWith('$') ? '' : ' ';
	return `${symbol}${separator}${amount.toFixed(2)}`;
}

function formatLevelLabel(level: string): string {
	if (LEVEL_LABELS[level]) return LEVEL_LABELS[level];
	const levelNumber = level?.replace('level_', '');
	return levelNumber ? `Nivel ${levelNumber}` : 'Sin nivel';
}

function sortLevels(levels: string[]): string[] {
	return [...levels].sort((a, b) => numberValue(a.replace('level_', '')) - numberValue(b.replace('level_', '')));
}

function getDeviceLabel(user: Pick<CollectionUser, 'device_model' | 'device_count'>): string {
	if ((user.device_count ?? 0) > 1) return 'Varios';
	return user.device_model?.trim() || '-';
}

function parseRow(row: any[], columnNames?: string[]): CollectionUser {
	const dateBought = textValue(getColumn(row, columnNames, ['Fecha Compra'], COLUMN_INDEX['Fecha Compra']));
	const status = textValue(getColumn(row, columnNames, ['Estado', 'Status'], COLUMN_INDEX.Estado)).toLowerCase();
	return {
		user_id: textValue(getColumn(row, columnNames, ['ID Usuario'], COLUMN_INDEX['ID Usuario'])),
		identification_number: textValue(getColumn(row, columnNames, ['Cedula'], COLUMN_INDEX.Cedula)),
		full_name: textValue(getColumn(row, columnNames, ['Nombre Completo'], COLUMN_INDEX['Nombre Completo'])),
		phone_number: textValue(getColumn(row, columnNames, ['Telefono'], COLUMN_INDEX.Telefono)),
		email: textValue(getColumn(row, columnNames, ['Email'], COLUMN_INDEX.Email)),
		device_model: textValue(getColumn(row, columnNames, ['Dispositivo'], COLUMN_INDEX.Dispositivo)),
		imei: textValue(getColumn(row, columnNames, ['IMEI'], COLUMN_INDEX.IMEI)),
		total_debt: numberValue(getColumn(row, columnNames, ['Deuda Vencida', 'Deuda Total'], COLUMN_INDEX['Deuda Vencida'])),
		paid_installments: numberValue(getColumn(row, columnNames, ['Cuotas Pagadas'], COLUMN_INDEX['Cuotas Pagadas'])),
		total_installments: numberValue(getColumn(row, columnNames, ['Cuotas Totales'], COLUMN_INDEX['Cuotas Totales'])),
		missing_payments: numberValue(getColumn(row, columnNames, ['Cuotas Vencidas'], COLUMN_INDEX['Cuotas Vencidas'])),
		days_overdue: numberValue(getColumn(row, columnNames, ['Dias Vencido'], COLUMN_INDEX['Dias Vencido'])),
		sucursal: textValue(getColumn(row, columnNames, ['Sucursal'], COLUMN_INDEX.Sucursal)),
		date_bought: STATUS_VALUES.has(dateBought.toLowerCase()) ? '' : dateBought,
		debt_date: textValue(getColumn(row, columnNames, ['Fecha Deuda'], COLUMN_INDEX['Fecha Deuda'])),
		user_level: textValue(getColumn(row, columnNames, ['Nivel'], COLUMN_INDEX.Nivel)) || 'level_1',
		device_count: numberValue(getColumn(row, columnNames, ['Cantidad Equipos'], COLUMN_INDEX['Cantidad Equipos'])),
		store_user: textValue(getColumn(row, columnNames, ['Vendedor'], COLUMN_INDEX.Vendedor)),
		seller_assignment: textValue(getColumn(row, columnNames, ['Asignación Vendedor'], COLUMN_INDEX['Asignación Vendedor'])),
		status: STATUS_VALUES.has(status) ? status : 'nuevo'
	};
}

function OverdueBadge({ days }: { days: number }) {
	let cls = 'text-yellow-500 bg-yellow-500/10';
	if (days > 60) cls = 'text-red-700 bg-red-500/20';
	else if (days > 30) cls = 'text-red-500 bg-red-500/10';
	else if (days > 15) cls = 'text-orange-500 bg-orange-500/10';
	return <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap ${cls}`}>{days}d vencido</span>;
}

function StatusBadge({ status }: { status: string }) {
	const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.nuevo;
	return <span className={`rounded-full px-3 py-0.5 text-xs font-semibold ${cfg.bg} ${cfg.color}`}>{cfg.label}</span>;
}

function SellerAssignmentBadge({ assignment }: { assignment?: string }) {
	if (!assignment || assignment === 'own') return null;
	const cfg = SELLER_ASSIGNMENT_CONFIG[assignment];
	if (!cfg) return null;
	return <span className={`rounded-md px-2 py-0.5 text-[10px] font-semibold ${cfg.bg} ${cfg.color}`}>{cfg.label}</span>;
}

function StatCard({
	Icon,
	label,
	value,
	sub,
	accent = 'from-primary-500 to-primary-400'
}: {
	Icon: React.ElementType;
	label: string;
	value: string | number;
	sub?: string;
	accent?: string;
}) {
	return (
		<div className="group/stat relative flex-1 min-w-[160px] rounded-xl bg-neutral-900/50 p-4 transition-all duration-200">
			<div className="absolute inset-0 rounded-xl ring-1 ring-inset ring-white/[0.04] transition-all duration-200 group-hover/stat:ring-white/[0.08]" />
			<div className="relative flex items-center gap-3">
				<div className={`flex size-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br ${accent} shadow-lg`}>
					<Icon className="size-5 text-white" />
				</div>
				<div className="min-w-0">
					<div className="text-xl font-bold text-neutral-100">{value}</div>
					<div className="text-xs text-neutral-500">{label}</div>
				</div>
			</div>
			{sub && <div className="relative mt-2 text-[11px] text-neutral-600">{sub}</div>}
		</div>
	);
}

function StatsSkeleton() {
	return (
		<div className="flex flex-wrap gap-4">
			{[0, 1, 2, 3].map((i) => (
				<div key={i} className="relative flex-1 min-w-[160px] rounded-xl bg-neutral-900/50 p-4 ring-1 ring-inset ring-white/[0.04]">
					<div className="flex items-center gap-3">
						<Skeleton className="size-10 rounded-xl" />
						<div className="flex-1 space-y-2">
							<Skeleton className="h-5 w-16" />
							<Skeleton className="h-3 w-24" />
						</div>
					</div>
					<Skeleton className="mt-2 h-3 w-20" />
				</div>
			))}
		</div>
	);
}

function UserListSkeleton() {
	return (
		<div className="space-y-2">
			{[0, 1, 2, 3, 4].map((i) => (
				<div key={i} className="flex items-center gap-4 rounded-xl bg-neutral-900/40 p-4 ring-1 ring-inset ring-white/[0.04]">
					<Skeleton className="size-11 rounded-xl" />
					<div className="flex-1 min-w-0 space-y-2">
						<div className="flex items-center gap-2">
							<Skeleton className="h-4 w-32" />
							<Skeleton className="h-4 w-16 rounded-full" />
						</div>
						<div className="flex gap-4">
							<Skeleton className="h-3 w-20" />
							<Skeleton className="h-3 w-24" />
							<Skeleton className="h-3 w-28" />
						</div>
					</div>
					<div className="space-y-2 text-right">
						<Skeleton className="h-5 w-16 ml-auto" />
						<Skeleton className="h-4 w-20 ml-auto rounded-full" />
					</div>
					<Skeleton className="size-5 rounded" />
				</div>
			))}
		</div>
	);
}

function EarningsSkeleton() {
	return (
		<>
			<div className="rounded-xl bg-neutral-900/50 p-7 space-y-5 ring-1 ring-inset ring-white/[0.04]">
				<div className="space-y-2">
					<Skeleton className="h-5 w-48" />
					<Skeleton className="h-3 w-20" />
				</div>
				<div className="flex flex-wrap gap-6">
					<div className="flex-1 min-w-[220px] rounded-xl bg-primary-500/5 p-6 space-y-3">
						<Skeleton className="h-3 w-32 mx-auto" />
						<Skeleton className="h-10 w-28 mx-auto" />
						<Skeleton className="h-3 w-24 mx-auto" />
					</div>
					<div className="flex-1 min-w-[220px] rounded-xl bg-neutral-800/30 p-6 space-y-3">
						<Skeleton className="h-3 w-36" />
						{[0, 1, 2].map((i) => (
							<div key={i} className="flex items-center justify-between py-2">
								<div className="space-y-1">
									<Skeleton className="h-4 w-28" />
									<Skeleton className="h-3 w-20" />
								</div>
								<Skeleton className="h-4 w-14" />
							</div>
						))}
					</div>
				</div>
			</div>
			<div className="rounded-xl bg-neutral-900/50 p-7 space-y-4 ring-1 ring-inset ring-white/[0.04]">
				<Skeleton className="h-5 w-40" />
				<div className="flex gap-4">
					<div className="flex-1 rounded-xl bg-amber-500/5 p-5 space-y-2">
						<Skeleton className="h-3 w-28 mx-auto" />
						<Skeleton className="h-7 w-20 mx-auto" />
					</div>
					<div className="flex-1 rounded-xl bg-emerald-500/5 p-5 space-y-2">
						<Skeleton className="h-3 w-28 mx-auto" />
						<Skeleton className="h-7 w-20 mx-auto" />
					</div>
				</div>
			</div>
		</>
	);
}

const SKELETON_BAR_HEIGHTS = [45, 72, 58, 88, 65, 50];

function HistorySkeleton() {
	return (
		<>
			<div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
				{[0, 1, 2].map((i) => (
					<div key={i} className="rounded-xl bg-neutral-900/50 p-5 space-y-2 ring-1 ring-inset ring-white/[0.04]">
						<Skeleton className="h-3 w-24 mx-auto" />
						<Skeleton className="h-8 w-16 mx-auto" />
						<Skeleton className="h-3 w-28 mx-auto" />
					</div>
				))}
			</div>
			<div className="rounded-xl bg-neutral-900/50 p-6 space-y-4 ring-1 ring-inset ring-white/[0.04]">
				<Skeleton className="h-5 w-40" />
				<Skeleton className="h-3 w-56" />
				<div className="flex items-end gap-3 h-40 px-2">
					{SKELETON_BAR_HEIGHTS.map((h, i) => (
						<div key={i} className="flex flex-1 flex-col items-center gap-1.5">
							<Skeleton className="h-3 w-6" />
							<Skeleton className="w-full max-w-[56px] rounded-t-lg" style={{ height: `${h}%` }} />
							<Skeleton className="h-3 w-8" />
						</div>
					))}
				</div>
			</div>
		</>
	);
}

function HistoryContent({ history, formatCurrency }: { history: HistoryData; formatCurrency: (amount: number) => string }) {
	const [showAll, setShowAll] = useState(false);
	const maxVal = Math.max(...history.months.map((x) => x.recovered), 1);
	const chartMonths = history.months.slice().reverse().slice(-6);
	const visibleMonths = showAll ? history.months : history.months.slice(0, 5);

	return (
		<>
			<div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
				<div className="rounded-xl bg-neutral-900/50 p-5 text-center ring-1 ring-inset ring-white/[0.04]">
					<div className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-2">Total recuperado</div>
					<div className="text-3xl font-extrabold text-neutral-100">{history.totals.total_recovered}</div>
					<div className="text-xs text-neutral-500">pagos recuperados</div>
				</div>
				<div className="rounded-xl bg-neutral-900/50 p-5 text-center ring-1 ring-inset ring-white/[0.04]">
					<div className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-2">Total ganado</div>
					<div className="text-3xl font-extrabold text-emerald-400">{formatCurrency(history.totals.total_earned)}</div>
					<div className="text-xs text-neutral-500">acumulado</div>
				</div>
				<div className="rounded-xl bg-neutral-900/50 p-5 text-center ring-1 ring-inset ring-white/[0.04]">
					<div className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-2">Promedio mensual</div>
					<div className="text-3xl font-extrabold text-primary-400">{history.averages.avg_recovered_per_month}</div>
					<div className="text-xs text-neutral-500">recuperaciones / mes</div>
				</div>
			</div>

			{chartMonths.length > 0 && (
				<div className="rounded-xl bg-neutral-900/50 p-6 ring-1 ring-inset ring-white/[0.04]">
					<h2 className="text-base font-bold text-neutral-100">Historial Mensual</h2>
					<p className="mt-1 text-xs text-neutral-500 mb-6">Recuperaciones de meses anteriores</p>
					<div className="flex items-end gap-3 h-40 px-2">
						{chartMonths.map((m, i) => {
							const pct = (m.recovered / maxVal) * 100;
							return (
								<div key={m.month} className="flex flex-1 flex-col items-center gap-1.5">
									<span className="text-xs font-bold text-neutral-200">{m.recovered}</span>
									<div
										className="w-full max-w-[56px] rounded-t-lg bg-gradient-to-t from-primary-600 to-primary-400"
										style={{ height: `${Math.max(pct, 8)}%`, minHeight: 16, opacity: 0.7 + i * 0.05 }}
									/>
									<span className="text-[10px] font-semibold text-neutral-500">
										{m.label.split(' ')[0]?.slice(0, 3)}
									</span>
								</div>
							);
						})}
					</div>
				</div>
			)}

			<div className="rounded-xl bg-neutral-900/50 p-6 ring-1 ring-inset ring-white/[0.04]">
				<h3 className="text-base font-bold text-neutral-100 mb-4">Detalle por mes</h3>

				<div className="overflow-hidden rounded-lg ring-1 ring-inset ring-white/[0.04]">
					<table className="w-full text-sm">
						<thead>
							<tr className="bg-neutral-800/40 text-[11px] font-bold uppercase tracking-wide text-neutral-500">
								<th className="py-2.5 pl-4 pr-2 text-left">Mes</th>
								<th className="px-2 py-2.5 text-center">Asignados</th>
								<th className="px-2 py-2.5 text-center">Contactados</th>
								<th className="px-2 py-2.5 text-center">Recuperados</th>
								<th className="px-2 py-2.5 text-center">Tasa</th>
								<th className="py-2.5 pl-2 pr-4 text-right">Ganado</th>
							</tr>
						</thead>
						<tbody className="divide-y divide-white/[0.03]">
							{visibleMonths.map((m, i) => {
								const rate = m.assigned ? Math.round(m.recovery_rate * 100) : 0;
								return (
									<tr key={m.month} className={`transition-colors hover:bg-white/[0.02] ${i === 0 ? 'bg-white/[0.02]' : ''}`}>
										<td className="py-3 pl-4 pr-2">
											<span className="font-semibold text-neutral-200">{m.label}</span>
											{i === 0 && (
												<span className="ml-2 rounded-md bg-blue-500/10 px-1.5 py-0.5 text-[10px] font-semibold text-blue-400">
													Último
												</span>
											)}
										</td>
										<td className="px-2 py-3 text-center text-neutral-400">{m.assigned}</td>
										<td className="px-2 py-3 text-center text-neutral-400">{m.contacted}</td>
										<td className="px-2 py-3 text-center">
											<span className="rounded-full bg-emerald-500/10 px-2.5 py-0.5 text-xs font-bold text-emerald-400">
												{m.recovered}
											</span>
										</td>
										<td className="px-2 py-3 text-center">
											<span className={`text-xs font-semibold ${rate >= 70 ? 'text-emerald-400' : rate >= 50 ? 'text-amber-400' : 'text-red-400'}`}>
												{rate}%
											</span>
										</td>
										<td className="py-3 pl-2 pr-4 text-right font-bold text-emerald-400">
											{formatCurrency(m.earned)}
										</td>
									</tr>
								);
							})}
						</tbody>
						<tfoot>
							<tr className="border-t-2 border-neutral-700 bg-neutral-800/30">
								<td className="py-3 pl-4 pr-2 font-extrabold text-neutral-100">Total acumulado</td>
								<td className="px-2 py-3 text-center font-bold text-neutral-200">{history.totals.total_assigned}</td>
								<td className="px-2 py-3 text-center font-bold text-neutral-200">{history.totals.total_contacted}</td>
								<td className="px-2 py-3 text-center font-extrabold text-emerald-400">{history.totals.total_recovered}</td>
								<td className="px-2 py-3 text-center font-bold text-emerald-400">{Math.round(history.totals.overall_recovery_rate * 100)}%</td>
								<td className="py-3 pl-2 pr-4 text-right font-extrabold text-emerald-400">{formatCurrency(history.totals.total_earned)}</td>
							</tr>
						</tfoot>
					</table>
				</div>

				{history.months.length > 5 && (
					<button
						onClick={() => setShowAll(!showAll)}
						className="mt-3 w-full rounded-lg py-2 text-xs font-semibold text-primary-300 transition-colors hover:bg-white/[0.03]"
					>
						{showAll ? 'Mostrar menos' : `Ver ${history.months.length - 5} meses más`}
					</button>
				)}
			</div>
		</>
	);
}

const RC_TABS = [
	{ id: 'lista', label: 'Cartera', icon: Users },
	{ id: 'ganancias', label: 'Ganancias', icon: DollarSign },
	{ id: 'historial', label: 'Historial', icon: TrendingUp }
];

export default function RecuperacionCartera() {
	const { userData, CountrySettings, LevelsConfig, internalCountry } = useContext(GlobalContext);
	const [featureEnabled, setFeatureEnabled] = useState<boolean | null>(null);
	const [activeTab, setActiveTab] = useState<'lista' | 'ganancias' | 'historial'>('lista');
	const [loading, setLoading] = useState(false);
	const [users, setUsers] = useState<CollectionUser[]>([]);
	const [totalCount, setTotalCount] = useState(0);
	const [page, setPage] = useState(0);
	const batchSize = 20;
	const [searchQuery, setSearchQuery] = useState('');
	const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
	const [statusFilter, setStatusFilter] = useState<string>('');
	const [minOverdueInstallments, setMinOverdueInstallments] = useState('');
	const [levelFilter, setLevelFilter] = useState('');
	const [minDebt, setMinDebt] = useState('');
	const [sortOption, setSortOption] = useState(SORT_OPTIONS[0].value);
	const [openFilter, setOpenFilter] = useState<'level' | 'payments' | 'debt' | null>(null);
	const fetchIdRef = useRef(0);
	const [stats, setStats] = useState<Stats | null>(null);
	const [earnings, setEarnings] = useState<Earnings | null>(null);
	const [history, setHistory] = useState<HistoryData | null>(null);

	const [selectedUser, setSelectedUser] = useState<UserDetail | null>(null);
	const [detailOpen, setDetailOpen] = useState(false);
	const [detailLoading, setDetailLoading] = useState(false);

	const [logContactOpen, setLogContactOpen] = useState(false);
	const [contactType, setContactType] = useState('llamada');
	const [contactNotes, setContactNotes] = useState('');

	const [addNoteOpen, setAddNoteOpen] = useState(false);
	const [noteText, setNoteText] = useState('');

	const [promiseOpen, setPromiseOpen] = useState(false);
	const [promiseAmount, setPromiseAmount] = useState('');
	const [promiseDate, setPromiseDate] = useState('');



	const [toast, setToast] = useState<string | null>(null);
	const [actionLoading, setActionLoading] = useState(false);
	const currencyCode = CountrySettings?.currency_code || '$';
	const formatCurrency = useCallback((amount: number) => formatCollectionCurrency(amount, currencyCode), [currencyCode]);
	const selectedSort = SORT_OPTIONS.find((option) => option.value === sortOption) ?? SORT_OPTIONS[0];
	const activeCountry = internalCountry.get() || userData?.country || 'venezuela';
	const levelOptions = useMemo(() => {
		const countryLevels = LevelsConfig ? ((LevelsConfig as Record<string, Record<string, unknown> | undefined>)[activeCountry] ?? {}) : {};
		const configuredLevels = sortLevels(Object.keys(countryLevels).filter((key) => /^level_\d+$/.test(key)));
		return configuredLevels.length ? configuredLevels : DEFAULT_LEVEL_OPTIONS;
	}, [LevelsConfig, activeCountry]);
	const selectedLevelLabel = levelFilter ? formatLevelLabel(levelFilter) : 'Nivel';
	const hasListFilters = Boolean(levelFilter || minOverdueInstallments || minDebt);

	const showToast = (msg: string) => {
		setToast(msg);
		setTimeout(() => setToast(null), 3000);
	};

	const handleSortClick = (control: (typeof SORT_CONTROLS)[number]) => {
		const isActive = selectedSort.column === control.column;
		const nextDirection = isActive ? (selectedSort.direction === 'ASC' ? 'DESC' : 'ASC') : control.defaultDirection;
		setSortOption(nextDirection === 'ASC' ? control.asc : control.desc);
		setPage(0);
	};

	const clearListFilters = () => {
		setLevelFilter('');
		setMinOverdueInstallments('');
		setMinDebt('');
		setOpenFilter(null);
		setPage(0);
	};

	const checkFeatureEnabled = useCallback(async () => {
		if (!userData) return;
		const result = await unsafeAPI('POST', `${API_BASE}/stores_collections/config/`, {}, userData, null);
		setFeatureEnabled(result?.enabled ?? false);
	}, [userData]);

	useEffect(() => {
		const timeout = window.setTimeout(() => {
			setDebouncedSearchQuery(searchQuery.trim());
			setPage(0);
		}, 350);
		return () => window.clearTimeout(timeout);
	}, [searchQuery]);

	const fetchUsers = useCallback(async () => {
		if (!userData) return;
		const requestId = ++fetchIdRef.current;
		setLoading(true);

		const filterBy = [];
		const minInstallments = numberValue(minOverdueInstallments);
		const minimumDebt = numberValue(minDebt);
		if (statusFilter) filterBy.push({ column_name: 'Estado', value: statusFilter });
		if (minInstallments > 0) filterBy.push({ column_name: 'Cuotas Vencidas', value: minInstallments });
		if (levelFilter) filterBy.push({ column_name: 'Nivel', value: levelFilter });
		if (minimumDebt > 0) filterBy.push({ column_name: 'Deuda Vencida', value: minimumDebt });

		const result = await unsafeAPI(
			'POST',
			`${API_BASE}/tables/store_collections_users/`,
			{
				master_store_email: userData?.master_store?.id,
				batch_size: batchSize,
				offset: page * batchSize,
				search_by: debouncedSearchQuery ? { column_name: 'Nombre Completo', value: debouncedSearchQuery } : {},
				order_by: { column_name: selectedSort.column, direction: selectedSort.direction },
				...(filterBy.length ? { filter_by: filterBy } : {})
			},
			userData,
			null
		);
		if (requestId !== fetchIdRef.current) return;
		if (result?.success) {
			setUsers((result.rows || []).map((row: any[]) => parseRow(row, result.column_names)));
			setTotalCount(result.total_count || 0);
		}
		setLoading(false);
	}, [userData, page, debouncedSearchQuery, selectedSort, statusFilter, minOverdueInstallments, minDebt, levelFilter]);

	const fetchStats = useCallback(async () => {
		if (!userData) return;
		const result = await unsafeAPI('POST', `${API_BASE}/stores_collections/stats/`, { sections: ['status_counts', 'earnings'] }, userData, null);
		if (result?.success) setStats(result.data);
	}, [userData]);

	const [earningsLoading, setEarningsLoading] = useState(false);
	const [historyLoading, setHistoryLoading] = useState(false);

	const fetchEarnings = useCallback(async () => {
		if (!userData) return;
		setEarningsLoading(true);
		const result = await unsafeAPI('POST', `${API_BASE}/stores_collections/earnings_current/`, {}, userData, null);
		if (result?.success) setEarnings(result.data);
		setEarningsLoading(false);
	}, [userData]);

	const fetchHistory = useCallback(async () => {
		if (!userData) return;
		setHistoryLoading(true);
		const result = await unsafeAPI('POST', `${API_BASE}/stores_collections/history/`, {}, userData, null);
		if (result?.success) setHistory(result.data);
		setHistoryLoading(false);
	}, [userData]);

	const fetchUserDetail = useCallback(
		async (user: CollectionUser) => {
			if (!userData) return;
			setDetailLoading(true);
			const result = await unsafeAPI(
				'POST',
				`${API_BASE}/stores_collections/delinquent_user_detail/`,
				{
					user_id: user.user_id,
					user_info: {
						user_id: user.user_id,
						identification_number: user.identification_number,
						full_name: user.full_name,
						phone_number: user.phone_number,
						email: user.email,
						device_model: user.device_model,
						imei: user.imei,
						total_debt: user.total_debt,
						paid_installments: user.paid_installments,
						total_installments: user.total_installments,
						missing_payments: user.missing_payments,
						days_overdue: user.days_overdue,
						sucursal: user.sucursal,
						date_bought: user.date_bought,
						debt_date: user.debt_date,
						user_level: user.user_level,
						device_count: user.device_count,
						store_user: user.store_user,
						seller_assignment: user.seller_assignment
					},
				},
				userData,
				null
			);
			if (result?.success) {
				setSelectedUser(result.data);
			}
			setDetailLoading(false);
		},
		[userData]
	);

	const [loadMoreLoading, setLoadMoreLoading] = useState(false);

	const loadMoreActivity = useCallback(async () => {
		if (!userData || !selectedUser) return;
		setLoadMoreLoading(true);
		const lastContact = selectedUser.contacts[selectedUser.contacts.length - 1];
		const lastNote = selectedUser.notes[selectedUser.notes.length - 1];
		const lastPromise = selectedUser.promises[selectedUser.promises.length - 1];
		const result = await unsafeAPI(
			'POST',
			`${API_BASE}/stores_collections/delinquent_user_detail/`,
			{
				user_id: selectedUser.user_id,
				...(selectedUser.contacts_has_more && lastContact ? { contacts_after: lastContact.id } : {}),
				...(selectedUser.notes_has_more && lastNote ? { notes_after: lastNote.id } : {}),
				...(selectedUser.promises_has_more && lastPromise ? { promises_after: lastPromise.id } : {}),
			},
			userData,
			null
		);
		if (result?.success) {
			setSelectedUser((prev) =>
				prev
					? {
							...prev,
							contacts: [...prev.contacts, ...(result.data.contacts || [])],
							contacts_has_more: result.data.contacts_has_more,
							notes: [...prev.notes, ...(result.data.notes || [])],
							notes_has_more: result.data.notes_has_more,
							promises: [...prev.promises, ...(result.data.promises || [])],
							promises_has_more: result.data.promises_has_more,
						}
					: prev
			);
		}
		setLoadMoreLoading(false);
	}, [userData, selectedUser]);

	useEffect(() => {
		checkFeatureEnabled();
	}, [checkFeatureEnabled]);

	useEffect(() => {
		if (featureEnabled) fetchUsers();
	}, [featureEnabled, fetchUsers]);

	useEffect(() => {
		if (featureEnabled) fetchStats();
	}, [featureEnabled, fetchStats]);

	useEffect(() => {
		if (featureEnabled && activeTab === 'ganancias') fetchEarnings();
		if (featureEnabled && activeTab === 'historial') fetchHistory();
	}, [featureEnabled, activeTab, fetchEarnings, fetchHistory]);

	const openDetail = (user: CollectionUser) => {
		setDetailOpen(true);
		fetchUserDetail(user);
	};

	const handleLogContact = async () => {
		if (!selectedUser || !userData) return;
		setActionLoading(true);
		const result = await unsafeAPI(
			'POST',
			`${API_BASE}/stores_collections/log_contact/`,
			{ user_id: selectedUser.user_id, type: contactType, notes: contactNotes },
			userData,
			null
		);
		if (result?.success) {
			showToast('Contacto registrado');
			setLogContactOpen(false);
			setContactNotes('');
			setDetailOpen(false);
			setUsers((prev) => prev.filter((u) => u.user_id !== selectedUser.user_id));
			setTotalCount((prev) => Math.max(0, prev - 1));
			fetchStats();
		}
		setActionLoading(false);
	};

	const handleAddNote = async () => {
		if (!selectedUser || !noteText.trim() || !userData) return;
		setActionLoading(true);
		const result = await unsafeAPI(
			'POST',
			`${API_BASE}/stores_collections/add_note/`,
			{ user_id: selectedUser.user_id, text: noteText.trim() },
			userData,
			null
		);
		if (result?.success) {
			showToast('Nota agregada');
			setAddNoteOpen(false);
			setNoteText('');
			fetchUserDetail(selectedUser);
		}
		setActionLoading(false);
	};

	const handleRegisterPromise = async () => {
		if (!selectedUser || !promiseAmount || !promiseDate || !userData) return;
		setActionLoading(true);
		const result = await unsafeAPI(
			'POST',
			`${API_BASE}/stores_collections/register_promise/`,
			{ user_id: selectedUser.user_id, amount: parseFloat(promiseAmount), promised_date: promiseDate },
			userData,
			null
		);
		if (result?.success) {
			showToast('Promesa de pago registrada');
			setPromiseOpen(false);
			setPromiseAmount('');
			setPromiseDate('');
			setDetailOpen(false);
			setUsers((prev) => prev.filter((u) => u.user_id !== selectedUser.user_id));
			setTotalCount((prev) => Math.max(0, prev - 1));
			fetchStats();
		}
		setActionLoading(false);
	};



	if (featureEnabled === null) return <ContainerSpinner />;
	if (!featureEnabled) {
		return (
			<div className="flex min-h-screen items-center justify-center px-4">
				<div className="w-full max-w-md">
					<div className="mb-6 flex flex-col items-center gap-4">
						<div className="relative">
							<div className="absolute -inset-1 rounded-full bg-gradient-to-r from-amber-400 to-orange-600 opacity-10 blur" />
							<div className="relative rounded-full bg-neutral-900 p-6 light:bg-neutral-1000 light:ring-2 light:ring-amber-500/10">
								<AlertTriangle className="h-8 w-8 text-amber-500" />
							</div>
						</div>
						<div className="space-y-2 text-center">
							<h1 className="text-2xl font-semibold tracking-tighter text-neutral-0 md:text-3xl">
								Recuperación de Cartera
							</h1>
							<p className="text-neutral-300">
								Esta funcionalidad no está disponible en este momento
							</p>
						</div>
					</div>

					<div className="space-y-4">
						<div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-4">
							<div className="flex items-start space-x-3">
								<div className="mt-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-amber-500/20">
									<Clock className="h-2.5 w-2.5 text-amber-400" />
								</div>
								<div className="flex-1">
									<p className="text-sm font-medium text-amber-300 light:text-amber-900">
										Servicio no disponible
									</p>
									<p className="text-sm text-amber-200/80 light:text-amber-900/80">
										La recuperación de cartera aún no está habilitada para tu tienda. Contacta a tu asesor de Krece para activar esta función.
									</p>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		);
	}

	const totalPages = Math.ceil(totalCount / batchSize);

	return (
		<div className="mx-auto w-full max-w-7xl space-y-6 p-4 md:p-6">
			{/* Title */}
			<div>
				<h1 className="text-2xl font-semibold text-neutral-100">Recuperación de Cartera</h1>
				<p className="mt-1 text-sm text-neutral-400">
					Gestiona los clientes morosos de tu tienda y gana {formatCurrency(stats?.earnings?.fee_per_recovery ?? 5)} por cada pago
					recuperado.
				</p>
			</div>

			{/* How tracking works */}
			<div className="flex items-start gap-3 rounded-xl border border-primary-500/20 bg-primary-500/[0.04] p-4 light:border-primary-300/40 light:bg-primary-50">
				<div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-primary-500/10 mt-0.5 light:bg-primary-100">
					<AlertTriangle className="size-4 text-primary-400 light:text-primary-600" />
				</div>
				<div className="text-sm text-primary-200/80 light:text-primary-900/80">
					<span className="font-semibold text-primary-300 light:text-primary-700">¿Cómo se registra una recuperación?</span>{' '}
					El sistema contabiliza un pago como recuperado por tu tienda cuando registras en la tarjeta del cliente moroso que fue gestionado por tu tienda y el cliente realiza el pago dentro de los próximos 7 días.
				</div>
			</div>

			{/* Stats */}
			{!stats ? (
				<StatsSkeleton />
			) : (
				<>
					<div className="flex flex-wrap gap-4">
						<StatCard
							Icon={Users}
							label="Clientes morosos"
							value={stats.total_assigned - stats.total_recovered}
							sub={`${stats.total_assigned} totales asignados`}
							accent="from-primary-500 to-primary-400"
						/>
						<StatCard
							Icon={Phone}
							label="Contactados"
							value={stats.total_contacted}
							sub={`${stats.by_status.nuevo ?? 0} sin contactar`}
							accent="from-amber-500 to-amber-400"
						/>
						<StatCard
							Icon={CheckCircle2}
							label="Recuperados"
							value={stats.total_recovered}
							sub={`${stats.by_status.promesa ?? 0} con promesa`}
							accent="from-emerald-500 to-emerald-400"
						/>
						<StatCard
							Icon={DollarSign}
							label="Ganado este mes"
							value={formatCurrency(stats.earnings.total_earned)}
							sub={`${formatCurrency(stats.earnings.fee_per_recovery)} por recuperación`}
							accent="from-primary-500 to-primary-400"
						/>
					</div>
				</>
			)}

			{/* Tabs */}
			<TabBar tabs={RC_TABS} activeTab={activeTab} onTabChange={(t) => setActiveTab(t as 'lista' | 'ganancias' | 'historial')} />

			{/* Lista tab */}
			{activeTab === 'lista' && (
				<div className="space-y-4">
					{/* Filters */}
					<div className="relative">
						<Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-neutral-500" />
						<input
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
							placeholder="Buscar por nombre, cédula o teléfono..."
							className="w-full rounded-lg bg-neutral-900/50 py-2.5 pl-9 pr-3 text-sm text-neutral-200 ring-1 ring-inset ring-white/[0.06] placeholder:text-neutral-600 outline-none transition-all focus:ring-primary-500/50"
						/>
					</div>

					<div className="flex flex-wrap items-center gap-2">
						{SORT_CONTROLS.map((control) => {
							const isActive = selectedSort.column === control.column;
							const DirectionIcon = isActive ? (selectedSort.direction === 'ASC' ? ArrowUp : ArrowDown) : ArrowUpDown;
							return (
								<button
									key={control.id}
									onClick={() => handleSortClick(control)}
									className={`flex h-9 items-center gap-2 rounded-lg border px-3 text-xs font-semibold transition-colors ${
										isActive
											? 'border-primary-500/40 bg-primary-500/15 text-primary-200'
											: 'border-white/[0.06] bg-neutral-900/50 text-neutral-400 hover:bg-neutral-800/60 hover:text-neutral-200'
									}`}
								>
									<span>{control.label}</span>
									<DirectionIcon className={`size-3.5 ${isActive ? 'text-primary-300' : 'text-neutral-600'}`} />
								</button>
							);
						})}

						<div className="hidden h-7 w-px bg-neutral-800 md:block" />

						<div className="relative">
							<button
								onClick={() => setOpenFilter(openFilter === 'level' ? null : 'level')}
								className={`flex h-9 items-center gap-2 rounded-lg border px-3 text-xs font-semibold transition-colors ${
									levelFilter
										? 'border-primary-500/40 bg-primary-500/15 text-primary-200'
										: 'border-white/[0.06] bg-neutral-900/50 text-neutral-400 hover:bg-neutral-800/60 hover:text-neutral-200'
								}`}
							>
								{selectedLevelLabel}
								<ChevronDown className="size-3.5 text-neutral-500" />
							</button>
							{openFilter === 'level' && (
								<div className="absolute left-0 top-10 z-20 w-44 rounded-xl border border-neutral-800 bg-neutral-950 p-1.5 shadow-2xl">
									<button
										onClick={() => {
											setLevelFilter('');
											setOpenFilter(null);
											setPage(0);
										}}
										className={`w-full rounded-lg px-3 py-2 text-left text-xs font-semibold transition-colors ${!levelFilter ? 'bg-primary-500/15 text-primary-200' : 'text-neutral-400 hover:bg-neutral-900 hover:text-neutral-200'}`}
									>
										Todos
									</button>
									{levelOptions.map((level) => (
										<button
											key={level}
											onClick={() => {
												setLevelFilter(level);
												setOpenFilter(null);
												setPage(0);
											}}
											className={`w-full rounded-lg px-3 py-2 text-left text-xs font-semibold transition-colors ${levelFilter === level ? 'bg-primary-500/15 text-primary-200' : 'text-neutral-400 hover:bg-neutral-900 hover:text-neutral-200'}`}
										>
											{formatLevelLabel(level)}
										</button>
									))}
								</div>
							)}
						</div>

						<div className="relative">
							<button
								onClick={() => setOpenFilter(openFilter === 'payments' ? null : 'payments')}
								className={`flex h-9 items-center gap-2 rounded-lg border px-3 text-xs font-semibold transition-colors ${
									minOverdueInstallments
										? 'border-primary-500/40 bg-primary-500/15 text-primary-200'
										: 'border-white/[0.06] bg-neutral-900/50 text-neutral-400 hover:bg-neutral-800/60 hover:text-neutral-200'
								}`}
							>
								Cuotas {minOverdueInstallments ? `≥ ${minOverdueInstallments}` : ''}
								<ChevronDown className="size-3.5 text-neutral-500" />
							</button>
							{openFilter === 'payments' && (
								<div className="absolute left-0 top-10 z-20 w-56 rounded-xl border border-neutral-800 bg-neutral-950 p-3 shadow-2xl">
									<label className="mb-2 block text-[11px] font-semibold text-neutral-500">Cuotas vencidas mínimas</label>
									<input
										type="text"
										inputMode="numeric"
										value={minOverdueInstallments}
										onChange={(e) => {
											setMinOverdueInstallments(e.target.value.replace(/\D/g, ''));
											setPage(0);
										}}
										placeholder="Ej. 2"
										className="w-full rounded-lg bg-neutral-900 px-3 py-2 text-sm font-semibold text-neutral-100 ring-1 ring-inset ring-white/[0.06] placeholder:text-neutral-600 outline-none focus:ring-primary-500/50"
									/>
									<div className="mt-2 flex gap-2">
										<button onClick={() => setOpenFilter(null)} className="flex-1 rounded-lg bg-primary-500/15 py-2 text-xs font-semibold text-primary-200 hover:bg-primary-500/20">
											Aplicar
										</button>
										<button
											onClick={() => {
												setMinOverdueInstallments('');
												setOpenFilter(null);
												setPage(0);
											}}
											className="flex-1 rounded-lg bg-neutral-900 py-2 text-xs font-semibold text-neutral-400 hover:text-neutral-200"
										>
											Limpiar
										</button>
									</div>
								</div>
							)}
						</div>

						<div className="relative">
							<button
								onClick={() => setOpenFilter(openFilter === 'debt' ? null : 'debt')}
								className={`flex h-9 items-center gap-2 rounded-lg border px-3 text-xs font-semibold transition-colors ${
									minDebt
										? 'border-primary-500/40 bg-primary-500/15 text-primary-200'
										: 'border-white/[0.06] bg-neutral-900/50 text-neutral-400 hover:bg-neutral-800/60 hover:text-neutral-200'
								}`}
							>
								Monto {minDebt ? `≥ ${currencyCode} ${minDebt}` : ''}
								<ChevronDown className="size-3.5 text-neutral-500" />
							</button>
							{openFilter === 'debt' && (
								<div className="absolute left-0 top-10 z-20 w-56 rounded-xl border border-neutral-800 bg-neutral-950 p-3 shadow-2xl">
									<label className="mb-2 block text-[11px] font-semibold text-neutral-500">Monto vencido mínimo</label>
									<input
										type="text"
										inputMode="decimal"
										value={minDebt}
										onChange={(e) => {
											setMinDebt(e.target.value.replace(/[^\d.]/g, ''));
											setPage(0);
										}}
										placeholder={`Ej. ${currencyCode} 100`}
										className="w-full rounded-lg bg-neutral-900 px-3 py-2 text-sm font-semibold text-neutral-100 ring-1 ring-inset ring-white/[0.06] placeholder:text-neutral-600 outline-none focus:ring-primary-500/50"
									/>
									<div className="mt-2 flex gap-2">
										<button onClick={() => setOpenFilter(null)} className="flex-1 rounded-lg bg-primary-500/15 py-2 text-xs font-semibold text-primary-200 hover:bg-primary-500/20">
											Aplicar
										</button>
										<button
											onClick={() => {
												setMinDebt('');
												setOpenFilter(null);
												setPage(0);
											}}
											className="flex-1 rounded-lg bg-neutral-900 py-2 text-xs font-semibold text-neutral-400 hover:text-neutral-200"
										>
											Limpiar
										</button>
									</div>
								</div>
							)}
						</div>

						{hasListFilters && (
							<button
								onClick={clearListFilters}
								className="h-9 rounded-lg border border-white/[0.06] bg-neutral-900/30 px-3 text-xs font-semibold text-neutral-500 transition-colors hover:bg-neutral-900 hover:text-neutral-200"
							>
								Limpiar filtros
							</button>
						)}
					</div>

					<div className="flex flex-wrap gap-2">
						{[
							{ value: '', label: 'Todos' },
							...Object.entries(STATUS_CONFIG).map(([value, cfg]) => ({ value, label: cfg.label }))
						].map((opt) => (
							<button
								key={opt.value}
								onClick={() => {
									setStatusFilter(opt.value);
									setPage(0);
								}}
								className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
									statusFilter === opt.value
										? 'bg-primary-500/20 text-primary-300 ring-1 ring-primary-500/40'
										: 'bg-neutral-900/50 text-neutral-400 ring-1 ring-inset ring-white/[0.06] hover:bg-neutral-800/60 hover:text-neutral-300'
								}`}
							>
								{opt.label}
								{opt.value && stats?.by_status?.[opt.value] != null && (
									<span className="ml-1.5 text-[10px] opacity-70">({stats.by_status[opt.value]})</span>
								)}
							</button>
						))}
					</div>

					{/* Results count */}
					<div className="text-xs font-medium text-neutral-500">
						Mostrando {users.length} de {totalCount} clientes
					</div>

					{/* User list */}
					{loading ? (
						<UserListSkeleton />
					) : users.length === 0 ? (
						<div className="flex flex-col items-center justify-center py-16 text-neutral-500">
							<Search className="size-10 mb-3" />
							<div className="text-sm font-semibold">No se encontraron clientes</div>
							<div className="text-xs">Intenta ajustar la búsqueda</div>
						</div>
					) : (
						<div className="space-y-2">
							{users.map((user) => (
								<div
									key={user.user_id}
									onClick={() => openDetail(user)}
									className="group/row flex cursor-pointer items-center gap-4 rounded-xl bg-neutral-900/40 p-4 ring-1 ring-inset ring-white/[0.04] transition-all hover:bg-neutral-900/60 hover:ring-primary-500/20"
								>
									<div className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-primary-500/20 to-primary-500/10 text-sm font-bold text-primary-400">
										{user.full_name
											.split(' ')
											.map((n) => n[0])
											.join('')
											.slice(0, 2)}
									</div>
									<div className="flex-1 min-w-0">
										<div className="flex items-center gap-2 mb-0.5">
											<span className="font-bold text-sm text-neutral-100 truncate">{user.full_name}</span>
											<StatusBadge status={user.status ?? 'nuevo'} />
											<SellerAssignmentBadge assignment={user.seller_assignment} />
										</div>
										<div className="flex gap-4 text-xs text-neutral-500">
											<span>{user.identification_number}</span>
											<span>{user.phone_number}</span>
											<span className="hidden sm:inline">{getDeviceLabel(user)}</span>
											<span className="hidden md:inline">{formatLevelLabel(user.user_level)}</span>
											{user.store_user && <span className="hidden max-w-[220px] truncate lg:inline">Vend: {user.store_user}</span>}
										</div>
									</div>
									<div className="text-right min-w-[90px]">
										<div className="font-extrabold text-base text-neutral-100">{formatCurrency(user.total_debt)}</div>
										<OverdueBadge days={user.days_overdue} />
									</div>
									<ChevronRight className="size-5 text-neutral-600 shrink-0 transition-transform group-hover/row:translate-x-0.5" />
								</div>
							))}
						</div>
					)}

					{/* Pagination */}
					{totalPages > 1 && (
						<div className="flex items-center justify-center gap-3 pt-2">
							<button
								disabled={page === 0}
								onClick={() => setPage((p) => Math.max(0, p - 1))}
								className="rounded-lg bg-neutral-900/50 p-2 text-neutral-400 ring-1 ring-inset ring-white/[0.06] transition-colors hover:bg-neutral-800 disabled:opacity-30"
							>
								<ChevronLeft className="size-4" />
							</button>
							<span className="text-xs font-medium text-neutral-500">
								{page + 1} / {totalPages}
							</span>
							<button
								disabled={page >= totalPages - 1}
								onClick={() => setPage((p) => p + 1)}
								className="rounded-lg bg-neutral-900/50 p-2 text-neutral-400 ring-1 ring-inset ring-white/[0.06] transition-colors hover:bg-neutral-800 disabled:opacity-30"
							>
								<ChevronRight className="size-4" />
							</button>
						</div>
					)}
				</div>
			)}

			{/* Ganancias tab */}
			{activeTab === 'ganancias' && (
				<div className="space-y-5">
					{earningsLoading ? (
						<EarningsSkeleton />
					) : !earnings ? (
						<div className="flex flex-col items-center justify-center py-16 text-neutral-500">
							<DollarSign className="size-10 mb-3" />
							<div className="text-sm font-semibold">No hay datos de ganancias disponibles</div>
							<div className="text-xs">Los datos aparecerán cuando se registren recuperaciones.</div>
						</div>
					) : (
						<>
							<div className="rounded-xl border border-neutral-800 bg-background p-7">
								<h2 className="text-lg font-bold text-neutral-100">Resumen de Ganancias</h2>
								<p className="mt-1 text-xs text-neutral-500">Mes actual</p>
								<div className="mt-5 flex flex-wrap gap-6">
									<div className="flex-1 min-w-[220px] rounded-xl bg-primary-500/10 p-6 text-center">
										<div className="text-xs font-semibold text-primary-400">Total ganado este mes</div>
										<div className="mt-2 text-4xl font-extrabold text-primary-300">{formatCurrency(earnings.total_earned)}</div>
										<div className="mt-1 text-xs text-primary-500">
											{earnings.total_recovered} pagos × {formatCurrency(earnings.fee_per_recovery)}
										</div>
									</div>
									<div className="flex-1 min-w-[220px] rounded-xl bg-neutral-900 p-6">
										<div className="text-xs font-semibold text-neutral-400 mb-4">Detalle de recuperaciones</div>
										{earnings.recoveries.length > 0 ? (
											earnings.recoveries.map((r, i) => (
												<div
													key={i}
													className="flex items-center justify-between border-b border-neutral-800 py-2 last:border-0"
												>
													<div>
														<div className="text-sm font-semibold text-neutral-200">{r.user_name}</div>
														<div className="text-xs text-neutral-500">{r.device}</div>
													</div>
													<span className="font-bold text-emerald-400">+{formatCurrency(r.fee_earned)}</span>
												</div>
											))
										) : (
											<div className="py-5 text-center text-xs text-neutral-500">Aún no hay recuperaciones este mes</div>
										)}
									</div>
								</div>
							</div>
							<div className="rounded-xl border border-neutral-800 bg-background p-7">
								<h3 className="text-base font-bold text-neutral-100 mb-4">Potencial de ganancias</h3>
								<div className="flex gap-4">
									<div className="flex-1 rounded-xl bg-amber-500/5 border border-amber-500/10 p-5 text-center">
										<div className="text-xs font-semibold text-amber-500">Si recuperas todos</div>
										<div className="mt-1 text-2xl font-extrabold text-amber-400">
											{formatCurrency(earnings.potential.if_all_recovered)}
										</div>
									</div>
									<div className="flex-1 rounded-xl bg-emerald-500/5 border border-emerald-500/10 p-5 text-center">
										<div className="text-xs font-semibold text-emerald-500">Con promesas actuales</div>
										<div className="mt-1 text-2xl font-extrabold text-emerald-400">
											{formatCurrency(earnings.potential.with_active_promises)}
										</div>
									</div>
								</div>
							</div>
						</>
					)}
				</div>
			)}

			{/* Historial tab */}
			{activeTab === 'historial' && (
				<div className="space-y-5">
					{historyLoading ? (
						<HistorySkeleton />
					) : !history ? (
						<div className="flex flex-col items-center justify-center py-16 text-neutral-500">
							<TrendingUp className="size-10 mb-3" />
							<div className="text-sm font-semibold">No hay historial disponible</div>
							<div className="text-xs">El historial aparecerá conforme se registren meses de actividad.</div>
						</div>
					) : (
						<HistoryContent history={history} formatCurrency={formatCurrency} />
					)}
				</div>
			)}

			{/* Detail Dialog */}
			{detailOpen && (
				<Dialog title={selectedUser?.full_name || 'Cargando...'} onClose={() => setDetailOpen(false)} className="!max-w-[660px]">
					{detailLoading || !selectedUser ? (
						<ContainerSpinner />
					) : (
						<div className="space-y-5">
							{/* Header */}
							<div className="flex gap-4">
								<div className="flex size-14 shrink-0 items-center justify-center rounded-xl bg-primary-500/10 text-lg font-bold text-primary-400">
									{selectedUser.full_name
										.split(' ')
										.map((n) => n[0])
										.join('')
										.slice(0, 2)}
								</div>
								<div className="flex-1">
									<div className="flex items-center gap-2 mb-1">
										<StatusBadge status={selectedUser.status} />
										<OverdueBadge days={selectedUser.days_overdue} />
										<SellerAssignmentBadge assignment={selectedUser.seller_assignment} />
									</div>
									<div className="text-xs text-neutral-500">
										{selectedUser.identification_number} • {selectedUser.email}
									</div>
								</div>
								<div className="text-right">
									<div className="text-2xl font-extrabold text-neutral-100">{formatCurrency(selectedUser.total_debt)}</div>
									<div className="text-xs text-neutral-500">deuda vencida</div>
								</div>
							</div>

							{/* Info grid */}
							<div className="grid grid-cols-2 gap-3">
								<div className="rounded-lg bg-neutral-900 p-3">
									<div className="text-[10px] font-semibold uppercase text-neutral-500 mb-1">Teléfono</div>
									<div className="flex items-center gap-2">
										<div className="text-sm font-semibold text-neutral-200">{selectedUser.phone_number}</div>
										<a
											href={(() => {
												const countryDialCodes: Record<string, string> = {
													venezuela: '58',
													rd: '1',
													peru: '51',
													colombia: '57',
													bolivia: '591',
												};
												const dialCode = countryDialCodes[userData?.country || ''] || '58';
												const rawPhone = selectedUser.phone_number.replace(/\D/g, '').replace(/^0+/, '');
												const storeName = userData?.store_name || userData?.master_store?.store_name || 'tu tienda';
												const overduePayments = Math.max(1, selectedUser.missing_payments || 0);
												const paymentText = overduePayments === 1 ? '1 pago vencido' : `${overduePayments} pagos vencidos`;
												const equipmentText = (selectedUser.device_count ?? 0) > 1 ? 'tus equipos' : 'tu equipo';
												const message = `Buenas ${selectedUser.full_name}, te contactamos de ${storeName}, porque tienes ${paymentText} por ${formatCurrency(selectedUser.total_debt)} en ${equipmentText} con Krece. ¿Cómo te podemos ayudar a ponerte al día?`;
												return `https://wa.me/${dialCode}${rawPhone}?text=${encodeURIComponent(message)}`;
											})()}
											target="_blank"
											rel="noopener noreferrer"
											className="flex size-6 shrink-0 items-center justify-center rounded-md bg-green-500/15 text-green-400 transition-colors hover:bg-green-500/25"
											title="Enviar WhatsApp"
										>
											<WhatsApp className="!text-[0.95rem]" />
										</a>
									</div>
								</div>
								<div className="rounded-lg bg-neutral-900 p-3">
									<div className="text-[10px] font-semibold uppercase text-neutral-500 mb-1">Dispositivo</div>
									<div className="text-sm font-semibold text-neutral-200">{getDeviceLabel(selectedUser)}</div>
								</div>
								<div className="rounded-lg bg-neutral-900 p-3">
									<div className="text-[10px] font-semibold uppercase text-neutral-500 mb-1">IMEI</div>
									<div className="text-xs font-mono text-neutral-300">{selectedUser.imei}</div>
								</div>
								<div className="rounded-lg bg-neutral-900 p-3">
									<div className="text-[10px] font-semibold uppercase text-neutral-500 mb-1">Cuotas vencidas</div>
									<div className="text-sm font-semibold text-neutral-200">
										{selectedUser.missing_payments} de {selectedUser.total_installments}
									</div>
								</div>
								<div className="rounded-lg bg-neutral-900 p-3">
									<div className="text-[10px] font-semibold uppercase text-neutral-500 mb-1">Sucursal</div>
									<div className="text-sm font-semibold text-neutral-200">{selectedUser.sucursal || '-'}</div>
								</div>
								<div className="rounded-lg bg-neutral-900 p-3">
									<div className="text-[10px] font-semibold uppercase text-neutral-500 mb-1">Vendedor</div>
									<div className="flex items-center gap-2 text-sm font-semibold text-neutral-200">
										<span className="truncate">{selectedUser.store_user || '-'}</span>
										<SellerAssignmentBadge assignment={selectedUser.seller_assignment} />
									</div>
								</div>
								<div className="rounded-lg bg-neutral-900 p-3">
									<div className="text-[10px] font-semibold uppercase text-neutral-500 mb-1">Fecha de compra</div>
									<div className="text-sm font-semibold text-neutral-200">{selectedUser.date_bought ? format_date(selectedUser.date_bought) : '-'}</div>
								</div>
							</div>

						{/* Promises */}
							{selectedUser.promises.length > 0 && (
								<div className="rounded-lg bg-violet-500/5 border border-violet-500/10 p-3">
									<div className="text-xs font-semibold text-violet-400 mb-1.5">Promesas de pago</div>
									{selectedUser.promises.map((p) => (
										<div key={p.id} className="text-xs text-violet-300">
											{formatCurrency(p.amount)} — {format_date(p.promised_date)}{' '}
											<span className="text-violet-500">({p.status})</span>
										</div>
									))}
									{selectedUser.promises_has_more && (
										<button
											onClick={loadMoreActivity}
											disabled={loadMoreLoading}
											className="mt-1.5 text-[11px] font-medium text-violet-400 hover:text-violet-300 disabled:opacity-50"
										>
											{loadMoreLoading ? 'Cargando...' : 'Ver más promesas'}
										</button>
									)}
								</div>
							)}

							{/* Actions */}
							<div className="flex flex-wrap gap-2">
								<Button
									variant="primary"
									onClick={() => setLogContactOpen(true)}
									Icon={<Phone className="size-4" />}
									className="!w-auto"
								>
									Registrar contacto
								</Button>
								<Button variant="secondary" onClick={() => setAddNoteOpen(true)} Icon={<FileText className="size-4" />} className="!w-auto">
									Agregar nota
								</Button>
								<Button variant="secondary" onClick={() => setPromiseOpen(true)} Icon={<Target className="size-4" />} className="!w-auto">
									Promesa de pago
								</Button>
							</div>

							{/* Timeline */}
							<div className="border-t border-neutral-800 pt-4">
								<div className="text-xs font-bold text-neutral-400 mb-3">Historial de actividad</div>
								{selectedUser.contacts.length === 0 && selectedUser.notes.length === 0 ? (
									<div className="py-6 text-center text-xs text-neutral-600">
										Sin actividad registrada. ¡Haz el primer contacto!
									</div>
								) : (
									<div className="space-y-2 max-h-[200px] overflow-y-auto">
										{[
											...selectedUser.contacts.map((c) => ({ ...c, kind: 'contact' as const, text: c.notes })),
											...selectedUser.notes.map((n) => ({ ...n, kind: 'note' as const }))
										]
											.sort((a, b) => (b.date || '').localeCompare(a.date || ''))
											.map((item, i) => {
												const ContactIcon = CONTACT_TYPES.find((c) => c.value === (item as any).type)?.Icon || Phone;
												return (
													<div key={i} className="flex gap-2.5 rounded-lg bg-neutral-900 p-2.5">
														{item.kind === 'contact' ? (
															<ContactIcon className="size-4 text-neutral-400 mt-0.5 shrink-0" />
														) : (
															<FileText className="size-4 text-neutral-400 mt-0.5 shrink-0" />
														)}
														<div className="flex-1 min-w-0">
															<div className="text-xs font-semibold text-neutral-200">
																{item.kind === 'contact'
																	? CONTACT_TYPES.find((c) => c.value === (item as any).type)?.label || 'Contacto'
																	: 'Nota'}
															</div>
															{item.text && (
																<div className="text-[11px] text-neutral-500 mt-0.5 truncate">{item.text}</div>
															)}
														</div>
														<span className="text-[10px] text-neutral-600 whitespace-nowrap shrink-0">
															{format_date(item.date)}
														</span>
													</div>
												);
											})}
										{(selectedUser.contacts_has_more || selectedUser.notes_has_more) && (
											<button
												onClick={loadMoreActivity}
												disabled={loadMoreLoading}
												className="w-full rounded-lg py-2 text-[11px] font-medium text-neutral-400 hover:text-neutral-200 disabled:opacity-50"
											>
												{loadMoreLoading ? 'Cargando...' : 'Cargar más actividad'}
											</button>
										)}
									</div>
								)}
							</div>
						</div>
					)}
				</Dialog>
			)}

			{/* Log Contact Dialog */}
			{logContactOpen && (
				<Dialog title="Registrar contacto" onClose={() => setLogContactOpen(false)} cerrarButton={false}>
					<div className="space-y-4">
						<div>
							<label className="text-xs font-semibold text-neutral-400 mb-2 block">Tipo de contacto</label>
							<div className="flex gap-2">
								{CONTACT_TYPES.map((ct) => (
									<button
										key={ct.value}
										onClick={() => setContactType(ct.value)}
										className={`flex-1 rounded-lg border p-3 text-center transition-colors ${
											contactType === ct.value
												? 'border-primary-500 bg-primary-500/10'
												: 'border-neutral-700 bg-neutral-900 hover:border-neutral-600'
										}`}
									>
										<ct.Icon className={`size-5 mx-auto mb-1 ${contactType === ct.value ? 'text-primary-400' : 'text-neutral-400'}`} />
										<div className={`text-xs font-semibold ${contactType === ct.value ? 'text-primary-400' : 'text-neutral-500'}`}>
											{ct.label}
										</div>
									</button>
								))}
							</div>
						</div>
						<div>
							<label className="text-xs font-semibold text-neutral-400 mb-2 block">Nota (opcional)</label>
							<textarea
								value={contactNotes}
								onChange={(e) => setContactNotes(e.target.value)}
								placeholder="Agregar una nota sobre este contacto..."
								className="w-full rounded-lg border border-neutral-700 bg-neutral-900 p-3 text-sm text-neutral-200 placeholder:text-neutral-600 outline-none focus:border-primary-500 min-h-[80px] resize-vertical"
							/>
						</div>
						<Button variant="primary" onClick={handleLogContact} loading={actionLoading}>
							Registrar contacto
						</Button>
					</div>
				</Dialog>
			)}

			{/* Add Note Dialog */}
			{addNoteOpen && (
				<Dialog title="Agregar nota" onClose={() => setAddNoteOpen(false)} cerrarButton={false}>
					<div className="space-y-4">
						<div>
							<label className="text-xs font-semibold text-neutral-400 mb-2 block">Nota</label>
							<textarea
								value={noteText}
								onChange={(e) => setNoteText(e.target.value)}
								placeholder="Escribe una nota sobre este cliente..."
								className="w-full rounded-lg border border-neutral-700 bg-neutral-900 p-3 text-sm text-neutral-200 placeholder:text-neutral-600 outline-none focus:border-primary-500 min-h-[100px] resize-vertical"
							/>
						</div>
						<Button variant="primary" onClick={handleAddNote} loading={actionLoading}>
							Guardar nota
						</Button>
					</div>
				</Dialog>
			)}

			{/* Promise Dialog */}
			{promiseOpen && (
				<Dialog title="Registrar promesa de pago" onClose={() => setPromiseOpen(false)} cerrarButton={false}>
					<div className="space-y-4">
						<div>
							<label className="text-xs font-semibold text-neutral-400 mb-2 block">Monto prometido ({currencyCode})</label>
							<input
								type="number"
								value={promiseAmount}
								onChange={(e) => setPromiseAmount(e.target.value)}
								placeholder="0.00"
								className="w-full rounded-lg border border-neutral-700 bg-neutral-900 p-2.5 text-sm text-neutral-200 outline-none focus:border-primary-500"
							/>
						</div>
						<div>
							<label className="text-xs font-semibold text-neutral-400 mb-2 block">Fecha prometida</label>
							<input
								type="date"
								value={promiseDate}
								onChange={(e) => setPromiseDate(e.target.value)}
								className="w-full rounded-lg border border-neutral-700 bg-neutral-900 p-2.5 text-sm text-neutral-200 outline-none focus:border-primary-500"
							/>
						</div>
						<Button variant="primary" onClick={handleRegisterPromise} loading={actionLoading}>
							Registrar promesa
						</Button>
					</div>
				</Dialog>
			)}

				{/* Toast */}
			{toast && (
				<div className="fixed bottom-6 right-6 z-[2000] flex items-center gap-2 rounded-xl bg-neutral-950 border border-neutral-800 px-5 py-3 text-sm font-semibold text-neutral-100 shadow-2xl animate-[fadeInUp_0.3s_ease]">
					<CheckCircle2 className="size-4 text-emerald-400" />
					{toast}
				</div>
			)}
		</div>
	);
}
